import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft, GitCommit } from 'lucide-react';
import { getPromptDiff, getPrompt } from '../services/api';

const PromptDiff = () => {
  const { promptId, version1, version2 } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('unified'); // 'unified' or 'split'

  useEffect(() => {
    loadDiffData();
  }, [promptId, version1, version2]);

  const loadDiffData = async () => {
    try {
      const [promptData, diff] = await Promise.all([
        getPrompt(promptId),
        getPromptDiff(promptId, version1, version2)
      ]);
      setPrompt(promptData);
      setDiffData(diff);
    } catch (error) {
      console.error('Failed to load diff data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderLineDiff = (lineDiff) => {
    return lineDiff.map((part, index) => {
      const lines = part.value.split('\n').filter(line => line !== '' || part.added || part.removed);
      
      return lines.map((line, lineIndex) => {
        if (line === '' && !part.added && !part.removed) return null;
        
        let className = 'px-4 py-1 font-mono text-sm';
        let prefix = ' ';
        
        if (part.added) {
          className += ' bg-green-100 text-green-800 border-l-4 border-green-400';
          prefix = '+';
        } else if (part.removed) {
          className += ' bg-red-100 text-red-800 border-l-4 border-red-400';
          prefix = '-';
        } else {
          className += ' text-gray-700';
        }
        
        return (
          <div key={`${index}-${lineIndex}`} className={className}>
            <span className="select-none text-gray-400 mr-4 font-bold">{prefix}</span>
            {line}
          </div>
        );
      });
    });
  };

  const renderWordDiff = (wordDiff) => {
    return (
      <div className="px-4 py-2 font-mono text-sm">
        {wordDiff.map((part, index) => {
          let className = '';
          if (part.added) {
            className = 'bg-green-100 text-green-800';
          } else if (part.removed) {
            className = 'bg-red-100 text-red-800';
          }
          
          return (
            <span key={index} className={className}>
              {part.value}
            </span>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!diffData || !prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Diff not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">
          Compare v{version1}...v{version2}
        </span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <Link 
              to={`/prompts/${promptId}`}
              className="btn-secondary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Prompt
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Comparing versions v{version1} and v{version2}
          </h1>
          <p className="text-gray-600 mt-1">
            Showing changes in {prompt.name}
            {diffData.oldVersion.branch && diffData.newVersion.branch && 
             diffData.oldVersion.branch !== diffData.newVersion.branch && (
              <span className="ml-2 text-sm">
                (across branches: {diffData.oldVersion.branch} → {diffData.newVersion.branch})
              </span>
            )}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('unified')}
            className={`px-3 py-1 text-sm rounded-md ${
              viewMode === 'unified' 
                ? 'bg-primary-100 text-primary-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unified
          </button>
          <button
            onClick={() => setViewMode('split')}
            className={`px-3 py-1 text-sm rounded-md ${
              viewMode === 'split' 
                ? 'bg-primary-100 text-primary-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Split
          </button>
        </div>
      </div>

      {/* Diff Stats */}
      <div className="flex items-center space-x-6 text-sm text-gray-600">
        <div className="flex items-center">
          <GitCommit className="h-4 w-4 mr-2" />
          {diffData.lineDiff.filter(part => part.added || part.removed).length} changes
        </div>
        <div className="flex items-center space-x-2">
          <span className="flex items-center text-green-600">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-1"></span>
            {diffData.lineDiff.filter(part => part.added).length} additions
          </span>
          <span className="flex items-center text-red-600">
            <span className="w-3 h-3 bg-red-500 rounded-full mr-1"></span>
            {diffData.lineDiff.filter(part => part.removed).length} deletions
          </span>
        </div>
      </div>

      {/* Diff Content */}
      <div className="space-y-6">
        {viewMode === 'unified' ? (
          <div className="card overflow-hidden">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Unified Diff</h3>
            </div>
            <div className="bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-2 text-sm">
                <div className="px-4 py-2 border-r border-gray-200">
                  <span className="text-red-600">- v{diffData.oldVersion.version}</span>
                  {diffData.oldVersion.branch && (
                    <span className="text-gray-500 ml-2">({diffData.oldVersion.branch})</span>
                  )}
                </div>
                <div className="px-4 py-2">
                  <span className="text-green-600">+ v{diffData.newVersion.version}</span>
                  {diffData.newVersion.branch && (
                    <span className="text-gray-500 ml-2">({diffData.newVersion.branch})</span>
                  )}
                </div>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {renderLineDiff(diffData.lineDiff)}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Old Version */}
            <div className="card overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-4 bg-red-50">
                <h3 className="text-lg font-semibold text-red-800">
                  v{diffData.oldVersion.version} (Original)
                  {diffData.oldVersion.branch && (
                    <span className="text-sm font-normal text-red-600 ml-2">
                      from {diffData.oldVersion.branch}
                    </span>
                  )}
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <pre className="px-4 py-2 font-mono text-sm whitespace-pre-wrap">
                  {diffData.oldVersion.content}
                </pre>
              </div>
            </div>

            {/* New Version */}
            <div className="card overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-4 bg-green-50">
                <h3 className="text-lg font-semibold text-green-800">
                  v{diffData.newVersion.version} (Updated)
                  {diffData.newVersion.branch && (
                    <span className="text-sm font-normal text-green-600 ml-2">
                      from {diffData.newVersion.branch}
                    </span>
                  )}
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <pre className="px-4 py-2 font-mono text-sm whitespace-pre-wrap">
                  {diffData.newVersion.content}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Word-level diff for fine-grained changes */}
        <div className="card overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Word-level Changes</h3>
            <p className="text-sm text-gray-600 mt-1">
              Highlighting individual word changes
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {renderWordDiff(diffData.wordDiff)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptDiff; 